import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2FormGrid,
  V2FormField,
  Icon,
  ConfirmDialog,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { logout } from '../../lib/auth.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { useShellOutletContext } from '../../shell/shellOutletContext.js';
import { PresetFace } from '../../lib/avatars.js';
import {
  ALLOWED_AVATAR_MIME_TYPES,
  AVATAR_MAX_BYTES,
  type AvatarPresetId,
  type BrandColor,
  type OwnProfilePatchBody,
  type OwnProfileResponse,
  type SidebarSkin,
  type ThemeMode,
} from '../../lib/types/membership.js';
import {
  AVATAR_PRESET_DEFS,
  BRAND_COLOR_DEFS,
  SIDEBAR_SKIN_DEFS,
  THEME_MODE_DEFS,
  avatarGradient,
} from './firstLoginProfileConstants.js';

type AvatarKind = 'none' | 'preset' | 'photo';

function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) return null;
  return { mimeType: match[1]!, base64: match[2]! };
}

/**
 * UI_REFERENCE_SOURCE: no dedicated design frame — this is the persistent,
 * always-editable home for the same fields `FirstLoginProfileFlow.tsx`
 * collects once during onboarding (that file's own doc comment covers the
 * canonical field set/behavior this page mirrors). Lives at `/account`
 * (moved from `/mobile/account`, previously a bare logout button) so
 * "حساب کاربری" has a real destination from both the mobile bottom nav
 * and the desktop topbar's user menu, not just a mobile-only stub.
 *
 * One combined "ذخیرهٔ تغییرات" button rather than per-field/per-step
 * saves (unlike the onboarding wizard, which saves per step as it
 * advances) — this is a settings page a user revisits, not a linear
 * flow, so batching every field into one PATCH is the simpler, more
 * honest model of what "save" means here.
 */
export function AccountPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { onAppearancePreview, onThemeModePreview } = useShellOutletContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const base = slug ? tenantBasePath(slug) : '';
  const me = useApiGet<OwnProfileResponse>(slug ? `${base}/api/v1/me` : null, [slug, refreshKey]);

  if (!slug) return null;

  if (me.status === 'loading') return <LoadingState />;
  if (me.status === 'error') {
    return (
      <StatePanel
        variant={me.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={me.error.status === 403 ? 'دسترسی ندارید' : 'مشکلی پیش آمد'}
      />
    );
  }

  return (
    <AccountPageForm
      key={refreshKey}
      slug={slug}
      base={base}
      profile={me.data}
      onSaved={() => setRefreshKey((k) => k + 1)}
      onAppearancePreview={onAppearancePreview}
      onThemeModePreview={onThemeModePreview}
      onLogout={() => navigate(`${base}/login`)}
    />
  );
}

function AccountPageForm({
  slug,
  base,
  profile,
  onSaved,
  onAppearancePreview,
  onThemeModePreview,
  onLogout,
}: {
  slug: string;
  base: string;
  profile: OwnProfileResponse;
  onSaved: () => void;
  onAppearancePreview: (appearance: { brandColor: BrandColor; sidebarSkin: SidebarSkin }) => void;
  onThemeModePreview: (mode: ThemeMode) => void;
  onLogout: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isDemo = slug === 'demo' || slug === 'demo-preview';
  const locked = !profile.canEditLockedFields || isDemo;

  const [first, setFirst] = useState(profile.firstName ?? '');
  const [last, setLast] = useState(profile.lastName ?? '');
  const [email, setEmail] = useState(profile.email ?? '');
  const [birthdate, setBirthdate] = useState(profile.birthdate ?? '');

  const [avatarKind, setAvatarKind] = useState<AvatarKind>(
    profile.avatarPreset ? 'preset' : profile.avatarPhotoDataUrl ? 'photo' : 'none',
  );
  const [avatarPreset, setAvatarPreset] = useState<AvatarPresetId | null>(profile.avatarPreset);
  const [avatarPhotoDataUrl, setAvatarPhotoDataUrl] = useState<string | null>(
    profile.avatarPhotoDataUrl,
  );
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [themeMode, setThemeModeState] = useState<ThemeMode>(profile.themeMode ?? 'light');
  const [brandColor, setBrandColorState] = useState<BrandColor>(profile.brandColor ?? 'indigo');
  const [sidebarSkin, setSidebarSkinState] = useState<SidebarSkin>(profile.sidebarSkin ?? 'brand');

  function setThemeMode(next: ThemeMode) {
    setThemeModeState(next);
    onThemeModePreview(next);
  }
  function setBrandColor(next: BrandColor) {
    setBrandColorState(next);
    onAppearancePreview({ brandColor: next, sidebarSkin });
  }
  function setSidebarSkin(next: SidebarSkin) {
    setSidebarSkinState(next);
    onAppearancePreview({ brandColor, sidebarSkin: next });
  }

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const initials = `${first.slice(0, 1)}${last.slice(0, 1)}`;
  const activePresetDef = avatarPreset
    ? AVATAR_PRESET_DEFS.find((a) => a.id === avatarPreset)
    : undefined;
  const bigBg =
    avatarKind === 'photo' && avatarPhotoDataUrl
      ? 'var(--sk-bg-canvas)'
      : activePresetDef
        ? avatarGradient(activePresetDef)
        : 'linear-gradient(158deg, var(--sk-brand-400), var(--sk-brand-600))';

  function avatarPatchValue(): NonNullable<OwnProfilePatchBody['avatar']> {
    if (avatarKind === 'preset' && avatarPreset) return { kind: 'preset', preset: avatarPreset };
    if (avatarKind === 'photo' && avatarPhotoDataUrl) {
      const parsed = parseDataUrl(avatarPhotoDataUrl);
      if (parsed && (ALLOWED_AVATAR_MIME_TYPES as readonly string[]).includes(parsed.mimeType)) {
        return {
          kind: 'photo',
          mimeType: parsed.mimeType as (typeof ALLOWED_AVATAR_MIME_TYPES)[number],
          base64: parsed.base64,
        };
      }
    }
    return { kind: 'none' };
  }

  function onPickFile(file: File | undefined) {
    setAvatarError(null);
    if (!file) return;
    if (!(ALLOWED_AVATAR_MIME_TYPES as readonly string[]).includes(file.type)) {
      setAvatarError('فقط تصویر PNG، JPEG یا WEBP پذیرفته می‌شود.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('حجم تصویر باید کمتر از ۲ مگابایت باشد.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPhotoDataUrl(String(reader.result));
      setAvatarKind('photo');
      setAvatarPreset(null);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (isDemo) {
      setError('در محیط دمو امکان تغییر اطلاعات حساب کاربری وجود ندارد.');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    const patch: OwnProfilePatchBody = {
      ...(locked ? {} : { firstName: first.trim(), lastName: last.trim() }),
      email: email.trim() === '' ? null : email.trim(),
      birthdate: birthdate || null,
      avatar: avatarPatchValue(),
      themeMode,
      brandColor,
      sidebarSkin,
    };
    try {
      await apiPatch<OwnProfileResponse>(`${base}/api/v1/me`, patch);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? 'ذخیره‌سازی انجام نشد. دوباره تلاش کنید.' : 'خطای شبکه.');
    } finally {
      setSaving(false);
    }
  }

  async function onConfirmLogout() {
    await logout(slug).catch(() => undefined);
    onLogout();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>
      {isDemo ? (
        <div
          role="status"
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            padding: '14px 18px',
            borderRadius: 'var(--sk-r-lg)',
            background: 'var(--sk-warning-soft, #fff8e6)',
            border: '1px solid var(--sk-warning-border, #f5d485)',
          }}
        >
          <span style={{ color: '#d97706', display: 'flex', flexShrink: 0 }}>
            <Icon name="warn" size="md" />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <strong style={{ fontSize: 13.5, color: '#92400e' }}>
              فضای کاری نمایشی (دمو)
            </strong>
            <span style={{ fontSize: 12.5, lineHeight: 1.8, color: '#78350f' }}>
              در محیط دمو اطلاعات حساب کاربری و تنظیمات ظاهری فقط‌خواندنی است و امکان ویرایش یا ذخیرهٔ تغییرات وجود ندارد.
            </span>
          </div>
        </div>
      ) : null}
      <V2Card>
        <V2CardHeader title="اطلاعات شخصی" />
        <div style={{ padding: '0 20px 20px' }}>
          {locked && !isDemo ? (
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '12px 14px',
                marginBottom: 14,
                borderRadius: 'var(--sk-r-md)',
                background: 'var(--sk-info-soft)',
              }}
            >
              <span style={{ color: '#2f6fa8', display: 'flex', paddingTop: 2 }}>
                <Icon name="shield" size="sm" />
              </span>
              <span style={{ fontSize: 12.5, lineHeight: 1.9, color: 'var(--sk-fg-muted)' }}>
                نام و نام خانوادگی شما توسط ادمین سازمان ثبت شده و تنها او می‌تواند آن را تغییر دهد.
              </span>
            </div>
          ) : null}
          <V2FormGrid>
            <V2FormField label="نام" required width="medium">
              <input
                className="sk-input"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                readOnly={locked}
                style={{ background: locked ? 'var(--sk-bg-canvas)' : undefined }}
              />
            </V2FormField>
            <V2FormField label="نام خانوادگی" required width="medium">
              <input
                className="sk-input"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                readOnly={locked}
                style={{ background: locked ? 'var(--sk-bg-canvas)' : undefined }}
              />
            </V2FormField>
            <V2FormField label="شماره تلفن همراه" width="medium">
              <input
                className="sk-input"
                value={profile.mobile ?? ''}
                readOnly
                dir="ltr"
                style={{ background: 'var(--sk-bg-canvas)', direction: 'ltr', textAlign: 'left' }}
              />
            </V2FormField>
            <V2FormField label="ایمیل" width="medium">
              <input
                className="sk-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="اختیاری"
                readOnly={isDemo}
                dir="ltr"
                style={{
                  background: isDemo ? 'var(--sk-bg-canvas)' : undefined,
                  direction: 'ltr',
                  textAlign: 'left',
                }}
              />
            </V2FormField>
            <V2FormField label="تاریخ تولد" width="medium">
              <JalaliDateInput value={birthdate} onChange={setBirthdate} disabled={isDemo} />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="عکس پروفایل" />
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <span
              style={{
                position: 'relative',
                width: 84,
                height: 84,
                borderRadius: '50%',
                overflow: 'hidden',
                background: bigBg,
                border: '1px solid var(--sk-border)',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontSize: 24,
                fontWeight: 600,
              }}
            >
              {avatarKind === 'photo' && avatarPhotoDataUrl ? (
                <img
                  src={avatarPhotoDataUrl}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : activePresetDef ? (
                <PresetFace def={activePresetDef} iconSize="lg" />
              ) : (
                initials || <Icon name="user" size="lg" />
              )}
            </span>
          </div>
          <div
            style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              disabled={isDemo}
              onChange={(e) => onPickFile(e.target.files?.[0])}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <V2Button
                variant="secondary"
                icon="upload"
                disabled={isDemo}
                onClick={() => fileInputRef.current?.click()}
              >
                آپلود عکس
              </V2Button>
              {avatarKind !== 'none' ? (
                <V2Button
                  variant="ghost"
                  disabled={isDemo}
                  onClick={() => {
                    if (isDemo) return;
                    setAvatarKind('none');
                    setAvatarPreset(null);
                    setAvatarPhotoDataUrl(null);
                  }}
                >
                  حذف عکس
                </V2Button>
              ) : null}
            </div>
            {avatarError ? (
              <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{avatarError}</span>
            ) : null}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
                gap: 10,
                maxWidth: 420,
              }}
            >
              {AVATAR_PRESET_DEFS.map((a) => {
                const selected = avatarKind === 'preset' && avatarPreset === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    disabled={isDemo}
                    onClick={() => {
                      if (isDemo) return;
                      setAvatarKind('preset');
                      setAvatarPreset(a.id);
                      setAvatarPhotoDataUrl(null);
                    }}
                    style={{
                      position: 'relative',
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      padding: 0,
                      overflow: 'hidden',
                      border: selected ? '2px solid var(--sk-brand-500)' : '1px solid transparent',
                      background: avatarGradient(a),
                      color: '#fff',
                      display: 'grid',
                      placeContent: 'center',
                      cursor: isDemo ? 'not-allowed' : 'pointer',
                      opacity: isDemo && !selected ? 0.6 : 1,
                    }}
                  >
                    <PresetFace def={a} iconSize="sm" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="ظاهر حساب" subtitle="این تنظیمات مخصوص حساب شماست." />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>حالت نمایش</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {THEME_MODE_DEFS.map((t) => {
                const selected = themeMode === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={isDemo}
                    onClick={() => {
                      if (isDemo) return;
                      setThemeMode(t.id);
                    }}
                    style={{
                      textAlign: 'start',
                      cursor: isDemo ? 'not-allowed' : 'pointer',
                      opacity: isDemo && !selected ? 0.6 : 1,
                      minHeight: 54,
                      padding: '11px 13px',
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        color: selected ? 'var(--sk-brand-600)' : 'var(--sk-fg-muted)',
                        display: 'flex',
                      }}
                    >
                      <Icon name={t.icon} size="sm" />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
                        {t.label}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>{t.hint}</span>
                    </span>
                    {selected ? (
                      <span style={{ color: 'var(--sk-brand-600)', display: 'flex' }}>
                        <Icon name="check" size="sm" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              رنگ اصلی حساب
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {BRAND_COLOR_DEFS.map((b) => {
                const selected = brandColor === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    disabled={isDemo}
                    onClick={() => {
                      if (isDemo) return;
                      setBrandColor(b.id);
                    }}
                    style={{
                      cursor: isDemo ? 'not-allowed' : 'pointer',
                      opacity: isDemo && !selected ? 0.6 : 1,
                      minHeight: 80,
                      padding: 10,
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: b.color,
                        display: 'grid',
                        placeContent: 'center',
                        color: '#fff',
                      }}
                    >
                      {selected ? <Icon name="check" size="sm" /> : null}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--sk-fg)' }}>
                      {b.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              پوستهٔ ساید‌بار و منوی اپ
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {SIDEBAR_SKIN_DEFS.map((p) => {
                const selected = sidebarSkin === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={isDemo}
                    onClick={() => {
                      if (isDemo) return;
                      setSidebarSkin(p.id);
                    }}
                    style={{
                      cursor: isDemo ? 'not-allowed' : 'pointer',
                      opacity: isDemo && !selected ? 0.6 : 1,
                      padding: 10,
                      borderRadius: 'var(--sk-r-lg)',
                      border: `1.5px solid ${selected ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                      background: selected
                        ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
                        : 'var(--sk-bg-canvas)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        height: 38,
                        borderRadius: 'var(--sk-r-sm)',
                        overflow: 'hidden',
                        display: 'flex',
                        border: '1px solid var(--sk-border)',
                      }}
                    >
                      <span style={{ flex: '0 0 34%', background: p.bg }} />
                      <span style={{ flex: 1, background: 'var(--sk-bg-canvas)' }} />
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--sk-fg)',
                      }}
                    >
                      {p.label}
                      {selected ? (
                        <span style={{ marginInlineStart: 'auto', color: 'var(--sk-brand-600)' }}>
                          <Icon name="check" size="sm" />
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </V2Card>

      {error ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '13px 16px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
          }}
        >
          <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
            <Icon name="x" size="sm" />
          </span>
          <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {saved ? (
          <span style={{ fontSize: 13, color: 'var(--sk-success, var(--sk-brand-600))' }}>
            ذخیره شد.
          </span>
        ) : null}
        <span style={{ marginInlineEnd: 'auto' }} />
        <V2Button
          variant="primary"
          icon="check"
          disabled={saving || isDemo}
          title={isDemo ? 'در محیط دمو امکان تغییر اطلاعات وجود ندارد' : undefined}
          onClick={() => void handleSave()}
        >
          {saving ? 'در حال ذخیره…' : isDemo ? 'غیرقابل تغییر در محیط دمو' : 'ذخیرهٔ تغییرات'}
        </V2Button>
      </div>

      <V2Card>
        <div style={{ padding: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <V2Button variant="danger" icon="logout" onClick={() => setConfirmingLogout(true)}>
            خروج از حساب
          </V2Button>
        </div>
      </V2Card>

      {confirmingLogout ? (
        <ConfirmDialog
          open
          title="خروج از حساب"
          message="از حساب خود خارج می‌شوید."
          danger
          confirmLabel="خروج"
          cancelLabel="انصراف"
          onConfirm={onConfirmLogout}
          onCancel={() => setConfirmingLogout(false)}
        />
      ) : null}
    </div>
  );
}
